import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Sun, Moon } from 'lucide-react';
import SearchResults, { SearchResult } from './SearchResults';
import { SidebarItem } from '../types/cockpit';
import { EnhancedDashboardTask, DashboardEmail } from '../types/api';
import { useTheme } from '../contexts/ThemeContext';

interface CockpitHeaderProps {
  leftSidebarCollapsed: boolean;
  rightSidebarCollapsed: boolean;
  searchExpanded: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'checking';
  onLeftSidebarToggle: () => void;
  onRightSidebarToggle: () => void;
  onSearchToggle: () => void;
  onSearchClose: () => void;
  canvasContent?: any;
  /** Sidebar data for search filtering */
  sidebarData?: {
    clients: SidebarItem[];
    deals: SidebarItem[];
    tasks: SidebarItem[];
    emails: SidebarItem[];
  };
  /** Full task data for enhanced search */
  fullTasksData?: EnhancedDashboardTask[];
  /** Full email data for enhanced search */
  fullEmailsData?: DashboardEmail[];
  /** Callback when a search result is selected */
  onSearchResultSelect?: (result: SearchResult) => void;
}

const CockpitHeader: React.FC<CockpitHeaderProps> = ({
  leftSidebarCollapsed,
  rightSidebarCollapsed,
  searchExpanded,
  connectionStatus,
  onLeftSidebarToggle,
  onRightSidebarToggle,
  onSearchToggle,
  onSearchClose,
  canvasContent,
  sidebarData = { clients: [], deals: [], tasks: [], emails: [] },
  fullTasksData = [],
  fullEmailsData = [],
  onSearchResultSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { toggleTheme, isDark } = useTheme();

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        onSearchClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchExpanded, onSearchClose]);

  // Clear search when closing
  const handleSearchClose = useCallback(() => {
    setSearchQuery('');
    onSearchClose();
  }, [onSearchClose]);

  // Handle result selection
  const handleResultSelect = useCallback((result: SearchResult) => {
    setSearchQuery('');
    onSearchClose();
    if (onSearchResultSelect) {
      onSearchResultSelect(result);
    }
  }, [onSearchClose, onSearchResultSelect]);
  return (
    <>
      {/* Full Width Top Bar */}
      <div className="canvas-header" style={{ position: 'relative', zIndex: 10 }}>
        {/* Left side controls */}
        <div className="canvas-nav-buttons">
          <button className="canvas-nav-btn" onClick={onLeftSidebarToggle}>
            <span className="material-symbols-outlined">
              {leftSidebarCollapsed ? 'menu' : 'menu_open'}
            </span>
          </button>

          {/* Logo and Title */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginLeft: '16px'
          }}>
            <img
              src="/PF_Logo-1-10.png"
              alt="Prosper Finance Logo"
              style={{
                height: '40px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
        </div>

        {/* Center - Title */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0
        }}>
          <div style={{
            color: 'var(--canvas-text-primary)',
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}>
            Lex AI
          </div>
        </div>

        {/* Right side controls */}
        <div className="canvas-controls">
          {/* Theme Toggle */}
          <button
            className="canvas-nav-btn"
            onClick={toggleTheme}
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            type="button"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Search Toggle */}
          <button
            className="canvas-nav-btn"
            onClick={onSearchToggle}
            title="Toggle Search"
            type="button"
          >
            <Search size={16} />
          </button>

          {/* Backend Status Indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            backgroundColor: 'var(--canvas-bg-secondary)',
            fontSize: '11px',
            margin: '0 12px'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: connectionStatus === 'connected' ? 'var(--canvas-success)' :
                               connectionStatus === 'disconnected' ? 'var(--canvas-danger)' : 'var(--canvas-warning)'
            }}></div>
            <span style={{ color: 'var(--canvas-text-secondary)' }}>
              {connectionStatus === 'connected' ? 'Online' :
               connectionStatus === 'disconnected' ? 'Offline' : 'Connecting'}
            </span>
          </div>

          <button className="canvas-nav-btn" onClick={onRightSidebarToggle}>
            <span className="material-symbols-outlined" style={{
              transform: rightSidebarCollapsed ? 'scaleX(-1)' : 'none'
            }}>
              {rightSidebarCollapsed ? 'menu_open' : 'menu_open'}
            </span>
          </button>
        </div>
      </div>

      {/* Collapsible Search Bar with Results */}
      {searchExpanded && (
        <div
          ref={searchContainerRef}
          style={{
            background: 'var(--canvas-bg-secondary)',
            borderBottom: '1px solid var(--canvas-border)',
            padding: '12px 24px',
            position: 'relative'
          }}
        >
          <SearchResults
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onClose={handleSearchClose}
            sidebarData={sidebarData}
            fullTasksData={fullTasksData}
            fullEmailsData={fullEmailsData}
            onResultSelect={handleResultSelect}
            placeholder={canvasContent ? `Search in '${canvasContent.title}'` : "Search or ask Lex AI..."}
          />
        </div>
      )}
    </>
  );
};

export default CockpitHeader;
